import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Collapse, Pagination, Alert } from 'react-bootstrap';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import {
    CAMPAIGN_RESULT_STATUS_LABELS,
    CampaignResultStatusBadge,
    CampaignStatusBadge,
} from './campaignDetailUtils';
import CampaignStatsFilters from './CampaignStatsFilters';
import { useCampaign } from './useCampaign';
import { toProperCase } from '../process/processQueueUtils';

const SORT_FIELDS = [
    { value: 'createdAt', label: 'Created' },
    { value: 'patientName', label: 'Patient Name' },
    { value: 'receiver', label: 'Receiver' },
    { value: 'status', label: 'Status' },
    { value: 'id', label: 'Result ID' },
    { value: 'patientId', label: 'Patient ID' },
];

const SEARCH_FIELDS = [
    { value: 'q', label: 'Patient / Receiver' },
    { value: 'patientName', label: 'Patient Name' },
    { value: 'receiver', label: 'Receiver' },
    { value: 'id', label: 'Result ID' },
    { value: 'patientId', label: 'Patient ID' },
];

const STATUS_OPTIONS = Object.entries(CAMPAIGN_RESULT_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
}));

function CampaignStats() {
    const { campaignId } = useParams();
    const { campaign, isLoading: campaignLoading, error: campaignError, refetch } = useCampaign(campaignId);

    const [itemsPerPage, setItemsPerPage] = useState(30);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [resultItems, setResultItems] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        sent: 0,
        read: 0,
        failed: 0,
        unsubscribed: 0,
    });
    const [statsLoading, setStatsLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [searchField, setSearchField] = useState('q');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('DESC');

    const isSms = campaign?.type === 'SMS';
    const receiverLabel = isSms ? 'Phone' : 'Email';

    const queryOptions = {
        sortBy,
        sortOrder,
        searchField,
        searchTerm: appliedSearch,
        statusFilter,
    };

    const fetchStats = useCallback(() => {
        if (!campaignId) return;

        setStatsLoading(true);
        campaignService
            .getCampaignResultStats(campaignId)
            .then((res) => {
                setStats({
                    total: res.data.total ?? 0,
                    sent: res.data.sent ?? 0,
                    read: res.data.read ?? 0,
                    failed: res.data.failed ?? 0,
                    unsubscribed: res.data.unsubscribed ?? 0,
                });
            })
            .catch(() => {
                setStats({
                    total: 0,
                    sent: 0,
                    read: 0,
                    failed: 0,
                    unsubscribed: 0,
                });
            })
            .finally(() => setStatsLoading(false));
    }, [campaignId]);

    const fetchResults = useCallback(() => {
        if (!campaignId) return;

        setIsLoading(true);
        setError(null);

        campaignService
            .getCampaignResult(campaignId, currentPage, itemsPerPage, queryOptions)
            .then((res) => {
                const content = res.data.content || [];
                setResultItems(content);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                setExpandedRowId(null);
                if (content.length === 0 && (appliedSearch || statusFilter)) {
                    setError('No campaign results match your filters.');
                }
            })
            .catch((err) => {
                setResultItems([]);
                if (err?.response?.status === 403) {
                    setError('You do not have permission to view campaign results.');
                } else {
                    setError('Failed to load campaign results. Please try again.');
                }
            })
            .finally(() => setIsLoading(false));
    }, [campaignId, currentPage, itemsPerPage, sortBy, sortOrder, appliedSearch, searchField, statusFilter]);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const handleRefresh = () => {
        refetch();
        fetchStats();
        fetchResults();
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setAppliedSearch(searchTerm.trim());
        setCurrentPage(0);
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setAppliedSearch('');
        setSearchField('q');
        setStatusFilter('');
        setSortBy('createdAt');
        setSortOrder('DESC');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = resultItems.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const DetailField = ({ label, children, mono, wide }) => (
        <div className={`${wide ? 'col-12 col-lg-6' : 'col-md-6 col-lg-4'} mb-3`}>
            <div className="text-muted small mb-1">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    const IdCell = ({ value }) => (
        <small className="font-monospace text-break d-inline-block" style={{ maxWidth: '100%' }}>
            {value || '—'}
        </small>
    );

    const formatCount = (value) => {
        const number = Number(value);
        if (!Number.isFinite(number)) {
            return '0';
        }
        return number.toLocaleString();
    };

    const summaryCards = isSms
        ? [
              { key: 'total', label: 'Total', value: stats.total, tone: 'total' },
              { key: 'sent', label: 'Sent', value: stats.sent, tone: 'sent' },
              { key: 'failed', label: 'Failed', value: stats.failed, tone: 'failed' },
              { key: 'read', label: 'Read', value: stats.read, tone: 'read' },
              { key: 'unsubscribed', label: 'Unsub', value: stats.unsubscribed, tone: 'unsubscribed' },
          ]
        : [
              { key: 'total', label: 'Total', value: stats.total, tone: 'total' },
              { key: 'sent', label: 'Sent', value: stats.sent, tone: 'sent' },
              { key: 'read', label: 'Read', value: stats.read, tone: 'read' },
              { key: 'failed', label: 'Failed', value: stats.failed, tone: 'failed' },
              { key: 'unsubscribed', label: 'Unsub', value: stats.unsubscribed, tone: 'unsubscribed' },
          ];

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h1 className="mb-0">
                    <i className="bi bi-bar-chart me-2"></i>
                    Campaign Statistics
                </h1>
                <div className="d-flex align-items-center gap-2">
                    <Link to="/campaign" className="campaign-queue-page__back d-inline-flex align-items-center gap-1">
                        <i className="bi bi-arrow-left"></i>
                        Back to campaigns
                    </Link>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={campaignLoading || isLoading || statsLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Refresh
                    </Button>
                </div>
            </div>

            {campaignLoading ? (
                <Loading />
            ) : campaignError ? (
                <Alert variant="warning">{campaignError}</Alert>
            ) : (
                campaign && (
                    <>
                        <div className="campaign-stats-header mb-3">
                            <div className="campaign-stats-header__identity">
                                <div className="campaign-stats-header__title-row">
                                    <span className="campaign-stats-header__name" title={campaign.name || 'Untitled campaign'}>
                                        {campaign.name || 'Untitled campaign'}
                                    </span>
                                    <span
                                        className={`badge campaign-queue-type-badge ${
                                            isSms
                                                ? 'bg-success bg-opacity-10 text-success'
                                                : 'bg-primary bg-opacity-10 text-primary'
                                        }`}
                                    >
                                        <i className={`bi ${isSms ? 'bi-chat-dots' : 'bi-envelope'} me-1`}></i>
                                        {campaign.type}
                                    </span>
                                    <span className="badge bg-light text-dark border campaign-queue-type-badge">
                                        {toProperCase(campaign.category)}
                                    </span>
                                    <CampaignStatusBadge
                                        status={campaign.status}
                                        statusCode={campaign.statusCode}
                                    />
                                </div>
                                <div className="campaign-stats-header__meta text-muted">
                                    <span>{campaign.clinicName || '—'}</span>
                                    {campaign.title ? (
                                        <>
                                            <span className="campaign-stats-header__dot" aria-hidden="true">
                                                ·
                                            </span>
                                            <span title={campaign.title}>{campaign.title}</span>
                                        </>
                                    ) : null}
                                    <span className="campaign-stats-header__dot" aria-hidden="true">
                                        ·
                                    </span>
                                    <span className="font-monospace" title={campaign.id}>
                                        {campaign.id || '—'}
                                    </span>
                                </div>
                            </div>

                            <div className="campaign-stats-header__metrics" aria-label="Campaign result summary">
                                {summaryCards.map((card) => (
                                    <div
                                        key={card.key}
                                        className={`campaign-stats-metric campaign-stats-metric--${card.tone}`}
                                    >
                                        <span className="campaign-stats-metric__value">{formatCount(card.value)}</span>
                                        <span className="campaign-stats-metric__label">{card.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="card shadow-sm border-0 campaign-queue-table-card">
                            <div className="card-body p-2 p-md-3">
                                <div className="campaign-stats-results-toolbar mb-2">
                                    <div className="campaign-stats-results-toolbar__title">
                                        <i className="bi bi-table text-primary"></i>
                                        <h5 className="card-title mb-0">
                                            Campaign Results
                                            <span className="text-muted fw-normal">
                                                ({formatCount(totalElements)} total)
                                            </span>
                                        </h5>
                                    </div>

                                    <CampaignStatsFilters
                                        searchTerm={searchTerm}
                                        onSearchTermChange={setSearchTerm}
                                        searchField={searchField}
                                        onSearchFieldChange={(value) => {
                                            setSearchField(value);
                                            setCurrentPage(0);
                                        }}
                                        searchFields={SEARCH_FIELDS}
                                        statusFilter={statusFilter}
                                        onStatusFilterChange={(value) => {
                                            setStatusFilter(value);
                                            setCurrentPage(0);
                                        }}
                                        statusOptions={STATUS_OPTIONS}
                                        sortBy={sortBy}
                                        onSortByChange={(value) => {
                                            setSortBy(value);
                                            setCurrentPage(0);
                                        }}
                                        sortFields={SORT_FIELDS}
                                        sortOrder={sortOrder}
                                        onSortOrderChange={(value) => {
                                            setSortOrder(value);
                                            setCurrentPage(0);
                                        }}
                                        onSubmit={handleSearchSubmit}
                                        onClear={handleClearFilters}
                                        canClear={Boolean(
                                            searchTerm ||
                                                appliedSearch ||
                                                statusFilter ||
                                                sortBy !== 'createdAt' ||
                                                sortOrder !== 'DESC' ||
                                                searchField !== 'q'
                                        )}
                                    />
                                </div>

                                {isLoading ? (
                                    <Loading />
                                ) : error && resultItems.length === 0 ? (
                                    <Alert variant="warning">{error}</Alert>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '120px' }}>Status</th>
                                                    <th>Patient</th>
                                                    <th>{receiverLabel}</th>
                                                    <th>Result ID</th>
                                                    <th>Patient ID</th>
                                                    <th style={{ width: '220px' }}>Created</th>
                                                    <th style={{ width: '56px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {resultItems.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={7} className="text-center text-muted py-5">
                                                            <i className="bi bi-inbox d-block fs-3 mb-2"></i>
                                                            {appliedSearch || statusFilter
                                                                ? 'No campaign results match your filters.'
                                                                : 'No campaign results found.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    resultItems.map((item) => (
                                                        <React.Fragment key={item.id}>
                                                            <tr
                                                                className={
                                                                    expandedRowId === item.id ? 'table-active' : ''
                                                                }
                                                            >
                                                                <td>
                                                                    <CampaignResultStatusBadge status={item.status} />
                                                                </td>
                                                                <td>
                                                                    <div className="fw-medium small">
                                                                        {item.patientName || '—'}
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <small className="text-break campaign-queue-receiver">
                                                                        {item.receiver || '—'}
                                                                    </small>
                                                                </td>
                                                                <td>
                                                                    <IdCell value={item.id} />
                                                                </td>
                                                                <td>
                                                                    <IdCell value={item.patientId} />
                                                                </td>
                                                                <td>
                                                                    <small>{item.createdAt || '—'}</small>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        variant={
                                                                            expandedRowId === item.id
                                                                                ? 'outline-secondary'
                                                                                : 'outline-primary'
                                                                        }
                                                                        size="sm"
                                                                        onClick={() => toggleRow(item.id)}
                                                                        title={
                                                                            expandedRowId === item.id
                                                                                ? 'Collapse'
                                                                                : 'Expand'
                                                                        }
                                                                        aria-expanded={expandedRowId === item.id}
                                                                    >
                                                                        <i
                                                                            className={`bi bi-chevron-${
                                                                                expandedRowId === item.id
                                                                                    ? 'up'
                                                                                    : 'down'
                                                                            }`}
                                                                        ></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                            <tr className="p-0 border-top-0">
                                                                <td colSpan={7} className="p-0 border-top-0">
                                                                    <Collapse in={expandedRowId === item.id}>
                                                                        <div className="p-3 bg-light border-top">
                                                                            <div className="row">
                                                                                <DetailField label="Patient" wide>
                                                                                    {item.patientName || '—'}
                                                                                </DetailField>
                                                                                <DetailField label={receiverLabel} wide>
                                                                                    {item.receiver || '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Result ID" mono wide>
                                                                                    {item.id}
                                                                                </DetailField>
                                                                                <DetailField label="Patient ID" mono wide>
                                                                                    {item.patientId}
                                                                                </DetailField>
                                                                                <DetailField label="Campaign ID" mono wide>
                                                                                    {item.campaignId}
                                                                                </DetailField>
                                                                                <DetailField label="Clinic ID" mono wide>
                                                                                    {item.clinicId}
                                                                                </DetailField>
                                                                                <DetailField label="Type">
                                                                                    {item.type || '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Status">
                                                                                    <CampaignResultStatusBadge
                                                                                        status={item.status}
                                                                                    />
                                                                                </DetailField>
                                                                                <DetailField label="Status Code">
                                                                                    {item.statusCode ?? '—'}
                                                                                </DetailField>
                                                                                <DetailField label="Created">
                                                                                    {item.createdAt || '—'}
                                                                                </DetailField>
                                                                            </div>
                                                                        </div>
                                                                    </Collapse>
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {resultItems.length > 0 && (
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3 pt-3 border-top">
                                        <div className="d-flex flex-wrap align-items-center gap-2">
                                            <span className="text-muted small">
                                                Showing {formatCount(startItem)}–{formatCount(endItem)} of{' '}
                                                {formatCount(totalElements)}
                                            </span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: 'auto' }}
                                                value={itemsPerPage}
                                                onChange={handleItemsPerPageChange}
                                            >
                                                <option value={10}>10 per page</option>
                                                <option value={30}>30 per page</option>
                                                <option value={50}>50 per page</option>
                                                <option value={100}>100 per page</option>
                                            </select>
                                        </div>
                                        {paginationControls}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
}

export default CampaignStats;
